import { useEffect, useState } from "react";
import { startFlowTelemetry, setFlowPoster } from "./services/flowTelemetry";
import { postFlowEvents } from "./services/flowApi";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AuthBridge } from "./context/AuthBridge";
import { ColorSchemeProvider } from "./context/ColorSchemeContext";
import { FeatureThemeProvider } from "./context/FeatureThemeContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { AccountProvider } from "./context/AccountContext";
import { ToastProvider } from "./context/ToastContext";
import { AppLayout } from "./components/layout/AppLayout";
import { LoginScreen } from "./pages/LoginScreen";
import { AuthBlockedScreen } from "./pages/AuthBlockedScreen";
import { FirstRunPage } from "./pages/FirstRunPage";
import { OnboardingTour } from "./components/theme/OnboardingTour";
import { WhatsNewModal } from "./components/theme/WhatsNewModal";
import { ThemeNudgeModal } from "./components/theme/ThemeNudgeModal";

import { DashboardPage } from "./pages/DashboardPage";
import { HiveListPage } from "./pages/HiveListPage";
import { AddHivePage } from "./pages/AddHivePage";
import { HiveDetailPage } from "./pages/HiveDetailPage";
import { HoneyPoolPage } from "./pages/business/HoneyPoolPage";
import { VendorsPage } from "./pages/business/VendorsPage";
import { ClientDetailPage } from "./pages/business/ClientDetailPage";
import { ExpendituresPage } from "./pages/business/ExpendituresPage";
import { OtherProductsPage } from "./pages/business/OtherProductsPage";
import { HarvestsPage } from "./pages/business/HarvestsPage";
import { EquipmentPage } from "./pages/business/EquipmentPage";
import { SuppliersPage } from "./pages/business/SuppliersPage";
import { RecurringOrdersPage } from "./pages/business/RecurringOrdersPage";
import { PnLPage } from "./pages/business/PnLPage";
/* ForecastPage is NOT imported -- the route redirects while the feature is
   hidden (#449), and importing a component nothing renders would leave an
   unused binding that lint is right to complain about.

   The FILE stays on purpose. It is the page #449 restores, and deleting it
   would mean rewriting it from the issue text later. It is therefore a
   DELIBERATE orphan: nothing imports it, which normally means invisible to
   the build and untested -- recorded here so the next orphan sweep finds
   this comment rather than a mystery. */
import { TeamDashboardPage } from "./pages/team/TeamDashboardPage";
import { TeamTasksPage } from "./pages/team/TeamTasksPage";
import { JournalPage } from "./pages/JournalPage";
import { ImportPage } from "./pages/ImportPage";
import { BloomCalendarPage } from "./pages/BloomCalendarPage";
import { ReportsPage } from "./pages/ReportsPage";
import { SupportPage } from "./pages/SupportPage";
import { SettingsPage } from "./pages/settings/SettingsPage";
import { InspectionChecklistsPage } from "./pages/InspectionChecklistsPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { onboardingApi } from "./services/onboardingApi";
import { otherAppApi } from "./services/userApi";

function AuthedApp() {
  const { authBlock } = useAuth();

  /*
   * Has this account been through first-run setup? (#222)
   *
   * Asked of the SERVER, not of localStorage. The existing OnboardingTour
   * remembers itself per browser, which is fine for a tour nobody has to
   * finish -- but setup creates real rows, and "have I set up my apiary"
   * must not come out differently on a second device or after clearing
   * site data.
   *
   * null while unknown: rendering the dashboard first and then replacing
   * it with a setup screen is a flash of the wrong app, and rendering
   * setup first would show it to every established user for a moment.
   */
  const [firstRun, setFirstRun] = useState(null);

  /* #379. The flush timer and the page-away hook, started once for the
     whole app -- one buffer, because two would each flush half the
     events and neither would know about the other's open flows.

     Outside the auth check on purpose: the flows worth measuring most
     are onboarding and import, which happen to a brand new account, and
     gating the timer behind a settled session would miss them. Nothing
     is sent until there is something to send. */
  useEffect(() => {
    /* The transport is handed in rather than imported by the telemetry
       module -- see setFlowPoster for why a measurement must not decide
       what every instrumented page depends on. */
    setFlowPoster(postFlowEvents);
    return startFlowTelemetry();
  }, []);

  useEffect(() => {
    if (authBlock) return;
    let cancelled = false;
    /* Leave a breadcrumb that this app was opened (#302), so the OTHER
       one knows whether to mention itself. Once per session, best-effort,
       nothing waits for it: without this the reminder cannot tell a
       first-timer from somebody who works on both every day. */
    otherAppApi.appSeen("web").catch(() => {});
    onboardingApi
      .state()
      .then((r) => { if (!cancelled) setFirstRun(r); })
      // Best effort. If this call fails the app must still open -- being
      // unable to ask whether setup is needed is not a reason to withhold
      // an apiary somebody already has.
      .catch(() => { if (!cancelled) setFirstRun({ needed: false }); });
    return () => { cancelled = true; };
  }, [authBlock]);

  // Ahead of the providers deliberately. FeatureThemeProvider and
  // SubscriptionProvider both fetch on mount, so leaving them mounted
  // against a dead-end 401 means the same rejection over and over behind a
  // screen that is only there to explain it once (#166).
  if (authBlock) return <AuthBlockedScreen />;

  if (firstRun === null) return null;

  return (
    <AccountProvider>
    <FeatureThemeProvider>
      <SubscriptionProvider>
        {firstRun.needed ? (
          /* Instead of the shell, not inside it. A setup screen wrapped in
             navigation invites wandering off into pages that are empty
             precisely because setup has not happened yet. */
          <FirstRunPage
            hiveLimit={firstRun.hive_limit}
            onDone={(r) => {
              setFirstRun({ needed: false });
              // Land on the hive, not the dashboard: the dashboard of a
              // one-hive apiary is a summary of one thing.
              if (r?.land_on_hive_id) window.location.assign(`/hives/${r.land_on_hive_id}`);
            }}
          />
        ) : (
        <AppLayout>
          <OnboardingTour />
          <WhatsNewModal />
          <ThemeNudgeModal />
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/hives" element={<HiveListPage />} />
            <Route path="/hives/new" element={<AddHivePage />} />
            <Route path="/hives/:hiveId" element={<HiveDetailPage />} />

            {/* The old honey inventory page is gone (V042, #218). Redirect
                rather than 404: it was a real tab for months, so testers
                have it bookmarked and in browser history, and landing on
                "not found" reads as the app losing their honey rather than
                the page having moved. */}
            <Route path="/business/honey" element={<Navigate to="/business/pool" replace />} />
            <Route path="/business/pool" element={<HoneyPoolPage />} />
            <Route path="/business/vendors" element={<VendorsPage />} />
            {/* CLIENTS WAS THE OLD NAME FOR VENDORS (#418 stage 1).
                ClientsPage is deleted: it listed and created clients, and
                VendorsPage does both plus vendor balances, so it was
                strictly superseded. Nothing linked here -- the sidebar has
                pointed at /business/vendors for some time -- but a
                bookmark should not 404, so the path redirects. */}
            <Route path="/business/clients" element={<Navigate to="/business/vendors" replace />} />
            {/* THIS ONE STAYS, AND DELETING IT WOULD BE A BUG (#431).
                It looks as dead as ClientsPage did -- routed, unlinked,
                superseded in appearance by VendorDetail. It is not.

                It is the only caller of POST /clients/{id}/transactions,
                which is the only writer of app.Transactions -- now the
                manual path for income that is not honey (#430: wax,
                propolis, nucs, pollination). Removing it takes away the
                only way to record that income at all.

                PARTLY ANSWERED, AND STILL NOT DELETABLE (3 Oct). #430
                slice 2 gave WAX, PROPOLIS AND POLLEN a real home -- the
                generalised chain, sized and pooled and sold like honey.
                The other two did not come with it: nucs and pollination
                were split to #478 as post-release, so app.Transactions
                is still the ONLY way to record their income, and this
                page is still its only writer.
                
                So the condition on this comment is now #478, not #430.
                Worth being exact about, because "delete it when #430
                lands" reads as satisfied and #430 HAS landed -- and the
                deletion it invites would make the P&L's manual income
                permanently unwritable. That is the trap #431 found the
                first time, from the other direction.
                
                Until then this is reachable only by typing a URL, which
                is its own problem and now #478's to solve. */}
            <Route path="/business/clients/:clientId" element={<ClientDetailPage />} />
            <Route path="/business/expenditures" element={<ExpendituresPage />} />
            {/* The other-products door (#430 slice 2). The Sidebar hides
                its link until the account has a non-honey product, but the
                ROUTE is unconditional -- a bookmark or a link somebody
                kept must not 404, and the page says what to do when there
                is nothing to show. */}
            <Route path="/business/products" element={<OtherProductsPage />} />
            <Route path="/business/harvests" element={<HarvestsPage />} />
            <Route path="/business/equipment" element={<EquipmentPage />} />
            <Route path="/business/suppliers" element={<SuppliersPage />} />
            <Route path="/business/recurring-orders" element={<RecurringOrdersPage />} />
            <Route path="/business/pnl" element={<PnLPage />} />
            {/* FOLDED INTO P&L (#453), so the answer is on the page a
                bookmark should now land on rather than a 404. Same
                treatment as /business/clients and /business/forecast. */}
            <Route path="/business/break-even" element={<Navigate to="/business/pnl" replace />} />
            {/* HIDDEN, NOT DELETED (#449). The page is unreachable from the
                nav; the path redirects rather than 404s, for the reason
                /business/clients does two dozen lines up -- a bookmark that
                used to work should land somewhere, and Profit & Loss is the
                adjacent money question and the one that now reads REAL
                income since #431.

                ForecastPage.jsx and the endpoint both stay. This is a
                decision about what is offered, not a deletion, and #449 is
                where it comes back. */}
            <Route path="/business/forecast" element={<Navigate to="/business/pnl" replace />} />

            <Route path="/team" element={<TeamDashboardPage />} />
            <Route path="/team/tasks" element={<TeamTasksPage />} />
            <Route path="/journal" element={<JournalPage />} />
            <Route path="/import" element={<ImportPage />} />
            {/* Invite emails sent before 2026-08-14 point here, and it was
                never a route -- so every one of them dropped the recipient
                on the dashboard with nothing to accept. New emails link to
                /team; this catches the ones already in people's inboxes.
                Kept indefinitely: an invite email can sit unread for weeks,
                and a dead link is how someone concludes the invite was
                fake. */}
            <Route path="/account/shares" element={<Navigate to="/team" replace />} />

            <Route path="/bloom" element={<BloomCalendarPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/checklists" element={<InspectionChecklistsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/settings/*" element={<SettingsPage />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AppLayout>
        )}
      </SubscriptionProvider>
    </FeatureThemeProvider>
    </AccountProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthBridge />
      <ColorSchemeProvider>
        <ToastProvider>
          <AuthenticatedTemplate>
            <BrowserRouter>
              <AuthedApp />
            </BrowserRouter>
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <LoginScreen />
          </UnauthenticatedTemplate>
        </ToastProvider>
      </ColorSchemeProvider>
    </AuthProvider>
  );
}
